import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useAlert } from '../contexts/AlertContext';
import { apiService } from '../services/api';
import { PaymentRecord } from '../types';
import { Loading } from '../components';
import { FileText, Download, PackageOpen } from 'lucide-react';
import { formatDate as fmtDate, parseServerDate } from '../utils/date';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

const OrdersPage: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { showAlert } = useAlert();
  const [orders, setOrders] = useState<PaymentRecord[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user?.id) {
      loadOrders();
    }
  }, [user]);

  const loadOrders = async () => {
    if (!user?.id) return;
    
    try {
      setLoading(true);
      const response = await apiService.payments.getUserPayments(user.id);
      if (response.success && response.data) {
        // 按創建時間降序排序
        const sorted = [...response.data].sort((a, b) => {
          const dateA = (a.createdAt ? parseServerDate(a.createdAt) : new Date(0)).getTime();
          const dateB = (b.createdAt ? parseServerDate(b.createdAt) : new Date(0)).getTime();
          return dateB - dateA;
        });
        setOrders(sorted);
      }
    } catch (error: any) {
      console.error('Error loading orders:', error);
      showAlert({
        type: 'error',
        message: t('orders.errorLoad'),
      });
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status?.toUpperCase()) {
      case 'SUCCEEDED':
      case 'COMPLETED':
        return <Badge variant="default" className="bg-green-500 hover:bg-green-600">{t('orders.statusCompleted')}</Badge>;
      case 'PENDING':
        return <Badge variant="secondary" className="bg-yellow-500/20 text-yellow-700 hover:bg-yellow-500/30">{t('orders.statusPending')}</Badge>;
      case 'PROCESSING':
        return <Badge variant="secondary" className="bg-blue-500/20 text-blue-700 hover:bg-blue-500/30">{t('orders.statusProcessing')}</Badge>;
      case 'FAILED':
        return <Badge variant="destructive">{t('orders.statusFailed')}</Badge>;
      case 'EXPIRED':
        return <Badge variant="outline" className="text-orange-600 border-orange-300">{t('orders.statusExpired')}</Badge>;
      case 'CANCELLED':
      case 'CANCELED':
        return <Badge variant="outline" className="text-muted-foreground border-border">{t('orders.statusCancelled')}</Badge>;
      case 'REFUNDED':
        return <Badge variant="outline" className="text-purple-500 border-purple-300">{t('orders.statusRefunded')}</Badge>;
      default:
        return <Badge variant="outline">{status || t('orders.statusUnknown')}</Badge>;
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status?.toUpperCase()) {
      case 'SUCCEEDED':
      case 'COMPLETED':
        return t('orders.statusCompleted');
      case 'PENDING':
        return t('orders.statusPending');
      case 'PROCESSING':
        return t('orders.statusProcessing');
      case 'FAILED':
        return t('orders.statusFailed');
      case 'EXPIRED':
        return t('orders.statusExpired');
      case 'CANCELLED':
      case 'CANCELED':
        return t('orders.statusCancelled');
      case 'REFUNDED':
        return t('orders.statusRefunded');
      default:
        return status || t('orders.statusUnknown');
    }
  };

  const formatCurrency = (amount: number, currency: string) => {
    const symbol = currency === 'HKD' ? 'HK$' : currency === 'USD' ? 'US$' : '$';
    return `${symbol}${amount.toLocaleString()}`;
  };


  const handleDownloadInvoice = async (order: PaymentRecord) => {
    try {
      // 生成 invoice PDF
      const invoiceData = {
        orderId: order.airwalletOrderId || `ORDER-${order.id}`,
        transactionId: order.airwalletTransactionId,
        amount: order.amount,
        currency: order.currency,
        planName: order.vipPlan?.name || t('orders.vipPlan', 'VIP 方案'),
        planDuration: order.vipPlan?.durationDays || 0,
        status: order.status,
        createdAt: order.createdAt,
        paidAt: order.paidAt,
        userEmail: user?.email,
        username: user?.username,
      };

      // 創建 invoice HTML
      const invoiceHTML = `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="UTF-8">
          <title>${t('orders.invoiceTitle')} - ${invoiceData.orderId}</title>
          <script src="https://cdn.tailwindcss.com"></script>
        </head>
        <body class="bg-white p-10 text-slate-900">
          <div class="mb-8 border-b-2 border-slate-800 pb-5">
            <div class="text-3xl font-bold">${t('orders.invoiceTitle')}</div>
            <div class="mt-5 space-y-2.5">
              <div>
                <span class="inline-block w-40 font-bold">${t('orders.orderNo')}:</span>
                <span>${invoiceData.orderId}</span>
              </div>
              ${invoiceData.transactionId ? `
              <div>
                <span class="inline-block w-40 font-bold">${t('orders.invoiceTransactionId')}:</span>
                <span>${invoiceData.transactionId}</span>
              </div>
              ` : ''}
              <div>
                <span class="inline-block w-40 font-bold">${t('orders.invoiceDate')}:</span>
                <span>${fmtDate(invoiceData.paidAt || invoiceData.createdAt)}</span>
              </div>
            </div>
          </div>
          
          <div class="mt-8">
            <h3 class="text-lg font-semibold">${t('orders.invoiceBillTo')}</h3>
            <div class="mt-3">
              <span class="inline-block w-40 font-bold">${t('orders.invoiceName')}:</span>
              <span>${invoiceData.username || '-'}</span>
            </div>
            <div class="mt-2.5">
              <span class="inline-block w-40 font-bold">${t('orders.invoiceEmail')}:</span>
              <span>${invoiceData.userEmail || '-'}</span>
            </div>
          </div>
          
          <div class="mt-8">
            <h3 class="text-lg font-semibold">${t('orders.invoiceItems')}</h3>
            <table class="mt-5 w-full border-collapse">
              <thead class="bg-slate-100 font-bold">
                <tr>
                  <th class="border border-slate-300 px-3 py-3 text-left">${t('orders.invoiceDescription')}</th>
                  <th class="border border-slate-300 px-3 py-3 text-left">${t('orders.invoiceDuration')}</th>
                  <th class="border border-slate-300 px-3 py-3 text-left">${t('orders.invoiceAmount')}</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td class="border border-slate-300 px-3 py-3">${invoiceData.planName}</td>
                  <td class="border border-slate-300 px-3 py-3">${invoiceData.planDuration} ${t('vipPlan.days')}</td>
                  <td class="border border-slate-300 px-3 py-3">${formatCurrency(invoiceData.amount, invoiceData.currency)}</td>
                </tr>
              </tbody>
            </table>
          </div>
          
          <div class="mt-8 text-right text-xl font-bold">
            <div>${t('orders.invoiceTotal')}: ${formatCurrency(invoiceData.amount, invoiceData.currency)}</div>
            <div class="mt-2.5 text-sm font-medium text-slate-500">
              ${t('orders.invoiceStatus')}: ${getStatusLabel(invoiceData.status)}
            </div>
          </div>
          
          <div class="mt-12 border-t border-slate-300 pt-5 text-xs text-slate-500">
            <p>${t('orders.invoiceThanks')}</p>
            <p>${t('orders.invoiceAuto')}</p>
          </div>
        </body>
        </html>
      `;

      // 打開新窗口顯示 invoice
      const printWindow = window.open('', '_blank');
      if (printWindow) {
        printWindow.document.write(invoiceHTML);
        printWindow.document.close();
        
        // 等待內容載入後打印
        printWindow.onload = () => {
          printWindow.print();
        };
      }
    } catch (error: any) {
      console.error('Error generating invoice:', error);
      showAlert({
        type: 'error',
        message: t('orders.errorInvoice'),
      });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-64px)]">
        <Loading fullScreen message={t('common.loading')} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="text-xl">{t('orders.title')}</CardTitle>
          <p className="text-sm text-muted-foreground">{t('orders.subtitle')}</p>
        </CardHeader>
        <CardContent className="p-0">
          {orders.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center px-4">
              <div className="h-20 w-20 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                <PackageOpen className="h-10 w-10 text-primary" />
              </div>
              <h2 className="text-xl font-semibold mb-2">{t('orders.noOrders')}</h2>
              <p className="text-muted-foreground max-w-md mb-6">
                {t('orders.noOrdersDesc')}
              </p>
              <Button onClick={() => window.location.href = '/subscription'}>
                {t('orders.viewVipPlans')}
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50 hover:bg-muted/50">
                  <TableHead className="w-[180px]">{t('orders.orderNo')}</TableHead>
                  <TableHead>{t('orders.planDetails')}</TableHead>
                  <TableHead>{t('orders.amount')}</TableHead>
                  <TableHead>{t('orders.status')}</TableHead>
                  <TableHead>{t('orders.createdAt')}</TableHead>
                  <TableHead className="text-right">{t('orders.actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {orders.map((order) => (
                  <TableRow key={order.id} className="group">
                    <TableCell className="font-medium text-foreground">
                      {order.airwalletOrderId || `ORDER-${order.id}`}
                    </TableCell>
                    <TableCell>
                      <div className="font-medium">{order.vipPlan?.name || '-'}</div>
                      <div className="text-xs text-muted-foreground">{order.vipPlan?.durationDays || 0} {t('vipPlan.days')}</div>
                    </TableCell>
                    <TableCell className="font-semibold text-primary">
                      {formatCurrency(order.amount, order.currency)}
                    </TableCell>
                    <TableCell>
                      {getStatusBadge(order.status)}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {fmtDate(order.createdAt)}
                    </TableCell>
                    <TableCell className="text-right">
                      {(order.status?.toUpperCase() === 'SUCCEEDED' ||
                        order.status?.toUpperCase() === 'COMPLETED') && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-8 shadow-sm transition-all hover:shadow hover:bg-primary/5 hover:text-primary border-border"
                          onClick={() => handleDownloadInvoice(order)}
                        >
                          <Download className="mr-1.5 h-3.5 w-3.5" />
                          {t('orders.downloadInvoice')}
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default OrdersPage;
